import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, DatePicker, Dropdown, Empty, Radio, Space } from 'antd';
import { CloseOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { UnmountClosed } from 'react-collapse';
import {
  StyledTransactionMatchingPanel,
  StyledTransactionMatchingPanelActions,
  StyledTransactionMatchingPanelContent,
  StyledTransactionMatchingPanelAmountSpace,
  StyledTransactionMatchingPanelHeader,
  StyledTransactionMatchingPanelHeaderTitle,
  StyledTransactionMatchingPanelFilterSpace,
  StyledTransactionMatchingPanelRadioGroup,
  StyledTransactionMatchingPanelTransactionsContainer
} from './StyledTransactionMatchingPanel';
import Search from '../../../Search';
import TransactionDetails from '../TransactionDetails';
import { dateHelpers } from '../../../../utils/helpers';

const amountTypeOptions = [
  { key: '<', label: '≤' },
  { key: '>', label: '≥' }
];

const defaultFilter = {
  amount: '',
  amountType: '<',
  dateRange: null,
  search: ''
};

const searchProps = {
  allowClear: true,
  minSearchSymbolsLength: 1
};

const TransactionMatchingPanel = ({
  invoice,
  isAvailableSuggested = true,
  loading = false,
  onClose,
  onFilter,
  onSelect,
  transactions = { searched: [], suggested: [] },
  ...rest
}) => {
  const [t] = useTranslation('main');
  const [isSearchOpen, setIsSearchOpen] = useState(!isAvailableSuggested);
  const [filter, setFilter] = useState(defaultFilter);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isPaymentExternal, setIsPaymentExternal] = useState(false);

  const amountTypeLabel = useMemo(
    () => amountTypeOptions.find(({ key }) => key === filter.amountType)?.label || '',
    [filter.amountType]
  );

  // Reset filter when invoice changes
  useEffect(() => {
    setFilter(defaultFilter);
  }, [invoice]);

  // Deselect transaction if not found in new transactions
  useEffect(() => {
    if (!selectedTransaction) return;
    const exists =
      transactions.searched.some(tx => tx.id === selectedTransaction) ||
      transactions.suggested.some(tx => tx.id === selectedTransaction);
    if (!exists) {
      setSelectedTransaction(null);
      onSelect?.(undefined);
    }
  }, [transactions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Open search if no suggested available
  useEffect(() => {
    if (!isAvailableSuggested && !isSearchOpen) {
      setIsSearchOpen(true);
    }
  }, [isAvailableSuggested]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearchClick = (e) => {
    const value = !isSearchOpen;
    setIsSearchOpen(value);
    onClose?.(value);
    handleSelectTransaction(e);
  }

  const handleOnFilter = (key, value) => {
    const filterParams = {...filter, [key]: value};
    setFilter(filterParams);
    onFilter?.(filterParams);
  }

  const handleSelectTransaction = (e) => {
    const {value} = e.target;
    setSelectedTransaction(value);
    if (isPaymentExternal) {
      setIsPaymentExternal(false);
    }
    onSelect?.({type: 'transaction', value});
  }

  const handleSelectPaymentExternal = (e) => {
    const {checked} = e.target;
    setIsPaymentExternal(checked);
    if (checked && selectedTransaction) setSelectedTransaction(null);
    onSelect?.({type: 'external', value: checked});
  }

  const getTransactionContainer = (type) => {
    const transactionsList = transactions[type] || [];
    const isEmpty = !loading && !Boolean(transactionsList.length);
    return (
      <StyledTransactionMatchingPanelTransactionsContainer>
        {isEmpty ? (
          <Empty
            description={t('noTransactionsFoundForYourRequest')}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <StyledTransactionMatchingPanelRadioGroup
            onChange={handleSelectTransaction}
            value={selectedTransaction}
          >
            {transactionsList.map((transaction) => (
              <Radio value={transaction.id} key={transaction.id}>
                <TransactionDetails transaction={transaction} />
              </Radio>
            ))}
          </StyledTransactionMatchingPanelRadioGroup>
        )}
      </StyledTransactionMatchingPanelTransactionsContainer>
    )
  }

  return (
    <StyledTransactionMatchingPanel {...rest}>
      <StyledTransactionMatchingPanelHeader>
        <StyledTransactionMatchingPanelHeaderTitle>
          {t('haveYouAlreadyPaidIt')}
        </StyledTransactionMatchingPanelHeaderTitle>
        <Checkbox
          checked={isPaymentExternal}
          onChange={handleSelectPaymentExternal}
        >
          {t('invoices:paidOutsideWithLess')}
        </Checkbox>
      </StyledTransactionMatchingPanelHeader>

      <UnmountClosed isOpened={!isPaymentExternal}>
        <Space size='small' direction='vertical' style={{ width: '100%' }}>
          <StyledTransactionMatchingPanelActions>
            {!isSearchOpen && (
              <Button
                onClick={handleSearchClick}
                icon={<SearchOutlined />}
                type='link'
              >
                {t('searchTransactions')}
              </Button>
            )}
            {isSearchOpen && transactions.suggested.length !== 0 && (
              <Button
                onClick={handleSearchClick}
                type='link'
              >
                {t('suggestedMatched')}
              </Button>
            )}
            {selectedTransaction && (
              <Button
                onClick={handleSelectTransaction}
                icon={<CloseOutlined />}
                type='link'
              >
                {t('unselect')}
              </Button>
            )}
          </StyledTransactionMatchingPanelActions>

          <UnmountClosed isOpened={isSearchOpen}>
            <>
              <StyledTransactionMatchingPanelFilterSpace size='small'>
                <Search
                  {...searchProps}
                  defaultValue={filter.search}
                  onSearch={(value) => handleOnFilter('search', value)}
                  placeholder={t('search')}
                  value={filter.search}
                  size='middle'
                />
                <StyledTransactionMatchingPanelAmountSpace size={0}>
                  <Search
                    {...searchProps}
                    defaultValue={filter.amount}
                    placeholder={t('amount')}
                    onSearch={(value) => handleOnFilter('amount', value)}
                    type='number'
                    suffix={undefined}
                    style={{ width: '100%' }}
                    value={filter.amount}
                  />
                  <Dropdown
                    menu={{
                      items: amountTypeOptions,
                      onClick: (e) => handleOnFilter('amountType', e.key)
                    }}
                    trigger={['click']}
                  >
                    <Button>
                      {amountTypeLabel}
                    </Button>
                  </Dropdown>
                </StyledTransactionMatchingPanelAmountSpace>
                <DatePicker.RangePicker
                  disabledDate={(current) => current.isAfter(dayjs())}
                  panelRender={dateHelpers.getDatePickerPanelRender}
                  onChange={(value) => handleOnFilter('dateRange', value)}
                  format='DD MMM YYYY'
                  size='middle'
                  superNextIcon={false}
                  superPrevIcon={false}
                  value={filter.dateRange}
                />
              </StyledTransactionMatchingPanelFilterSpace>
              {getTransactionContainer('searched')}
            </>
          </UnmountClosed>
          <UnmountClosed isOpened={!isSearchOpen}>
            <StyledTransactionMatchingPanelContent>
              <StyledTransactionMatchingPanelHeader>
                <span>
                  {t('suggestedMatched')}
                </span>
              </StyledTransactionMatchingPanelHeader>
              {getTransactionContainer('suggested')}
            </StyledTransactionMatchingPanelContent>
          </UnmountClosed>
        </Space>
      </UnmountClosed>
    </StyledTransactionMatchingPanel>
  );
};

TransactionMatchingPanel.propTypes = {
  invoice: PropTypes.object,
  isAvailableSuggested: PropTypes.bool,
  loading: PropTypes.bool,
  onClose: PropTypes.func,
  onFilter: PropTypes.func,
  onSelect: PropTypes.func,
  onSearch: PropTypes.func,
  transactions: PropTypes.shape({
    searched: PropTypes.array,
    suggested: PropTypes.array,
  })
};

export default TransactionMatchingPanel;
